import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { loadGoogleMaps } from "@/lib/googleMaps";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "sonner";
import {
  MapPin, Truck, Package as PackageIcon, CircleNotch, ArrowsClockwise, Phone, Buildings,
} from "@phosphor-icons/react";

const COUNTRY_OPTIONS = [
  { code: "JM", name: "Jamaica", flag: "🇯🇲" },
  { code: "BS", name: "Bahamas", flag: "🇧🇸" },
  { code: "BB", name: "Barbados", flag: "🇧🇧" },
  { code: "TT", name: "Trinidad and Tobago", flag: "🇹🇹" },
  { code: "GY", name: "Guyana", flag: "🇬🇾" },
  { code: "KY", name: "Cayman Islands", flag: "🇰🇾" },
  { code: "DO", name: "Dominican Republic", flag: "🇩🇴" },
  { code: "HT", name: "Haiti", flag: "🇭🇹" },
  { code: "PR", name: "Puerto Rico", flag: "🇵🇷" },
];

const STATUS_META = {
  on_delivery: { label: "Delivering", color: "#2563eb", swatch: "bg-blue-500" },
  on_pickup:   { label: "On pickup",  color: "#d97706", swatch: "bg-amber-500" },
  online_idle: { label: "Online",     color: "#16a34a", swatch: "bg-emerald-500" },
  offline:     { label: "Offline",    color: "#71717a", swatch: "bg-zinc-400" },
};

const REFRESH_MS = 20_000;

export default function LiveMap() {
  const { user } = useAuth();
  const [country, setCountry] = useState("JM");
  const [data, setData] = useState({ drivers: [], counts: {}, bbox: null });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const googleRef = useRef(null);

  // Restrict access at page level
  const isAuthorized = user?.role === "super_admin";

  // Load Google Maps + initialize a single map instance
  useEffect(() => {
    if (!isAuthorized) return;
    let cancelled = false;
    (async () => {
      try {
        const g = await loadGoogleMaps();
        if (cancelled || !mapDivRef.current) return;
        // Ensure the `maps` library is available
        const { Map } = await g.maps.importLibrary("maps");
        await g.maps.importLibrary("marker");
        if (cancelled || !mapDivRef.current) return;
        const map = new Map(mapDivRef.current, {
          center: { lat: 18.1096, lng: -77.2975 }, // Jamaica default
          zoom: 9,
          mapId: "DEMO_MAP_ID", // generic styled id; safe to omit, just avoids advanced-markers warning
          disableDefaultUI: false,
          streetViewControl: false,
          mapTypeControl: false,
        });
        googleRef.current = g;
        mapRef.current = map;
      } catch (e) {
        console.error("Map load failed", e);
        toast.error("Couldn't load Google Maps. Check your network/API key.");
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthorized]);

  // Fetch driver snapshot + auto-refresh every REFRESH_MS while the page is open
  const fetchSnapshot = async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get(`/admin/live-drivers?country=${country}`);
      setData(payload);
    } catch (e) {
      toast.error("Failed to load drivers");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!isAuthorized) return undefined;
    fetchSnapshot();
    const t = setInterval(fetchSnapshot, REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [country, isAuthorized]);

  // Re-bound the map when country bbox changes (after a fresh snapshot)
  useEffect(() => {
    const g = googleRef.current;
    const map = mapRef.current;
    if (!g || !map) return;
    if (data.bbox) {
      const [s, w, n, e] = data.bbox;
      const bounds = new g.maps.LatLngBounds({ lat: s, lng: w }, { lat: n, lng: e });
      map.fitBounds(bounds, 24);
    }
  }, [data.bbox]);

  // Draw markers — diff against existing markers for smooth refresh
  useEffect(() => {
    const g = googleRef.current;
    const map = mapRef.current;
    if (!g || !map) return;
    const nextIds = new Set();
    (data.drivers || []).forEach((d) => {
      if (statusFilter !== "all" && d.status !== statusFilter) return;
      nextIds.add(d.id);
      const meta = STATUS_META[d.status] || STATUS_META.offline;
      const pos = { lat: d.lat, lng: d.lng };
      const existing = markersRef.current[d.id];
      if (existing) {
        existing.setPosition(pos);
        // Update icon if status changed
        if (existing._statusKey !== d.status) {
          existing.setIcon(makeMarkerIcon(g, meta.color));
          existing._statusKey = d.status;
        }
      } else {
        const m = new g.maps.Marker({
          position: pos,
          map,
          title: `${d.name} · ${meta.label}`,
          icon: makeMarkerIcon(g, meta.color),
        });
        m._statusKey = d.status;
        m.addListener("click", () => setSelected(d));
        markersRef.current[d.id] = m;
      }
    });
    // Remove stale markers
    Object.keys(markersRef.current).forEach((id) => {
      if (!nextIds.has(id)) {
        markersRef.current[id].setMap(null);
        delete markersRef.current[id];
      }
    });
  }, [data.drivers, statusFilter]);

  const filteredCount = useMemo(() => {
    if (statusFilter === "all") return (data.drivers || []).length;
    return (data.drivers || []).filter((d) => d.status === statusFilter).length;
  }, [data.drivers, statusFilter]);

  if (!isAuthorized) {
    return (
      <div className="p-12 text-center">
        <div className="text-sm text-zinc-500">Live Map is available to super admins only.</div>
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" />
      <div className="p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-zinc-500 mb-1">Operations</div>
            <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans" }} data-testid="live-map-title">
              Live Drivers Map
            </h1>
            <p className="text-xs text-zinc-500 mt-1">
              Real-time view of all drivers in the selected country. Heartbeat refreshes every 60 seconds; this view auto-refreshes every {REFRESH_MS / 1000}s.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-zinc-500 mb-1">Country</div>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger className="w-[220px]" data-testid="live-map-country-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTRY_OPTIONS.map((c) => (
                    <SelectItem key={c.code} value={c.code} data-testid={`live-country-${c.code}`}>
                      <span className="mr-1.5">{c.flag}</span> {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={fetchSnapshot} disabled={loading} data-testid="live-map-refresh-btn">
              {loading ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <ArrowsClockwise size={14} className="mr-1.5" weight="bold" />}
              Refresh
            </Button>
          </div>
        </div>

        {/* Status counts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="live-map-status-counts">
          {Object.entries(STATUS_META).map(([key, m]) => (
            <button
              key={key}
              type="button"
              onClick={() => setStatusFilter((s) => (s === key ? "all" : key))}
              className={`text-left bg-white dark:bg-zinc-900 border ${statusFilter === key ? "border-zinc-900 dark:border-white shadow-sm" : "border-zinc-200 dark:border-zinc-800"} rounded-md p-4 hover:border-zinc-400 transition-colors`}
              data-testid={`live-status-${key}`}
            >
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${m.swatch}`} />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{m.label}</span>
              </div>
              <div className="text-2xl font-semibold mt-1">{data.counts?.[key] || 0}</div>
            </button>
          ))}
        </div>

        {/* Map + side panel */}
        <div className="grid md:grid-cols-[1fr_320px] gap-4">
          <div className="relative bg-zinc-100 border border-zinc-200 rounded-md overflow-hidden" style={{ minHeight: 520 }} data-testid="live-map-container">
            <div ref={mapDivRef} className="w-full h-[600px]" />
            {(data.drivers || []).length === 0 && !loading && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="px-4 py-2 bg-white/90 rounded-md text-xs text-zinc-500 border border-zinc-200">
                  No drivers in {COUNTRY_OPTIONS.find((c) => c.code === country)?.name || country} yet.
                </div>
              </div>
            )}
          </div>

          <aside className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-4 max-h-[600px] overflow-y-auto" data-testid="live-map-driver-list">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                {statusFilter === "all" ? "All drivers" : STATUS_META[statusFilter].label}
              </div>
              <span className="text-xs text-zinc-500">{filteredCount}</span>
            </div>
            {(data.drivers || []).length === 0 ? (
              <div className="text-xs text-zinc-400 italic">No drivers reporting location yet.</div>
            ) : (
              (data.drivers || [])
                .filter((d) => statusFilter === "all" || d.status === statusFilter)
                .map((d) => (
                  <DriverRow key={d.id} driver={d} selected={selected?.id === d.id} onSelect={() => setSelected(d)} />
                ))
            )}
          </aside>
        </div>

        {/* Detail card under the map for the selected driver */}
        {selected && (
          <div className="mt-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5" data-testid="live-map-selected">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Selected driver</div>
                <div className="text-lg font-semibold mt-1">{selected.name}</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-zinc-600">
                  {selected.phone && <span className="inline-flex items-center gap-1"><Phone size={11} weight="bold" /> {selected.phone}</span>}
                  {selected.vehicle_type && <span className="inline-flex items-center gap-1"><Truck size={11} weight="bold" /> {selected.vehicle_type}</span>}
                  <span className="inline-flex items-center gap-1"><PackageIcon size={11} weight="bold" /> {selected.active_packages} active / {selected.active_pickups} pickups</span>
                  {selected.company_id && <span className="inline-flex items-center gap-1"><Buildings size={11} weight="bold" /> {selected.company_id === "zipexpress-network" ? "ZipExpress" : selected.company_id.slice(0, 8)}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${STATUS_META[selected.status]?.swatch} text-white`}>
                  {STATUS_META[selected.status]?.label}
                </span>
                <Button size="sm" variant="ghost" onClick={() => setSelected(null)} data-testid="live-map-close-selected">Close</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function DriverRow({ driver, selected, onSelect }) {
  const meta = STATUS_META[driver.status] || STATUS_META.offline;
  const seen = driver.last_seen_at ? new Date(driver.last_seen_at) : null;
  const seenRel = seen ? relative(seen) : "—";
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left px-3 py-2 rounded-md border ${selected ? "border-zinc-900 dark:border-white" : "border-transparent hover:border-zinc-200 dark:hover:border-zinc-700"} mb-1.5`}
      data-testid={`live-map-driver-${driver.id}`}
    >
      <div className="flex items-center gap-2.5">
        <span className={`w-2 h-2 rounded-full ${meta.swatch} shrink-0`} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{driver.name}</div>
          <div className="text-[11px] text-zinc-500 truncate">
            {meta.label} · {seenRel}
          </div>
        </div>
        <MapPin size={12} className="text-zinc-400 shrink-0" />
      </div>
    </button>
  );
}

function relative(dt) {
  const seconds = Math.max(0, Math.floor((Date.now() - dt.getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function makeMarkerIcon(g, color) {
  // Compact SVG truck pin — color-coded to the driver's status.
  // (We rely on the legacy Marker icon API instead of AdvancedMarkerElement
  //  so we can swap colors instantly without a mapId requirement.)
  return {
    path: "M 0,0 m -10,0 a 10,10 0 1,0 20,0 a 10,10 0 1,0 -20,0",
    fillColor: color,
    fillOpacity: 1,
    strokeColor: "#fff",
    strokeWeight: 2.5,
    scale: 1.15,
    anchor: new g.maps.Point(0, 0),
  };
}
